import debugFn from 'debug';
import { useEffect, useMemo, useRef } from 'react';
import { isCleanRoute } from '../helpers/is-clean-route';
import { useListViewStateStore } from '../stores/list-view-state.store';
import { FilterDefinition, ListViewFilters, ListViewSort } from '../types';
import { StoredListViewState } from '../stores/list-view-state.store';

const debug = debugFn('useStickyListViewState');

/**
 * Parameters structure that matches what comes from useQueryStates.
 * Includes filters, pageIndex, pageSize, and sort.
 */
type AllParams = {
  pageIndex: number;
  pageSize: number;
  sort: ListViewSort | null;
} & ListViewFilters;

/**
 * Return type for useStickyListViewState hook.
 */
export interface UseStickyListViewStateReturn {
  /**
   * Clear stored state for this listId.
   * Used by resetFilters to ensure next visit to clean route won't restore old filters.
   */
  clearStoredState: () => void;
}

/**
 * Hook that manages sticky ListView state persistence.
 * Handles hydration from storage on clean routes and syncing URL changes to storage.
 *
 * @param listId - Unique identifier for this ListView instance
 * @param allParams - Current URL parameters from useQueryStates
 * @param setAllParams - Function to update URL parameters
 * @param defaultPageSize - Default page size for clean route detection
 * @param filters - Filter definitions to extract filter keys
 * @param enabled - When false, skips hydration and persistence (URL defaults only)
 * @returns Object with clearStoredState function
 */
export function useStickyListViewState(
  listId: string,
  allParams: AllParams,
  setAllParams: (params: Partial<AllParams> | ((old: AllParams) => Partial<AllParams>)) => void,
  defaultPageSize: number,
  filters?: FilterDefinition[],
  enabled = true,
): UseStickyListViewStateReturn {
  // Get Zustand store actions for sticky state management
  const getStoredState = useListViewStateStore((state) => state.getStoredState);
  const setStoredState = useListViewStateStore((state) => state.setStoredState);
  const clearStoredState = useListViewStateStore((state) => state.clearStoredState);

  // Track if we just hydrated from storage to avoid syncing back immediately
  const hasHydratedRef = useRef(false);
  const isInitialMountRef = useRef(true);

  // Get filter keys for clean route detection
  const filterKeys = useMemo(() => (filters ?? []).map((f) => f.name), [filters]);

  // Initial hydration: On mount, check if URL is clean and hydrate from storage if needed
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Only run on initial mount
    if (!isInitialMountRef.current) {
      return;
    }
    isInitialMountRef.current = false;

    debug('Initial mount for listId: %s', listId);

    const isClean = isCleanRoute();
    debug('Route is clean: %s', isClean, {
      allParams,
      defaultPageSize,
      filterKeys,
    });

    if (isClean) {
      // URL is clean - check if we have stored state to restore
      const storedState = getStoredState(listId);
      debug('Checking stored state for listId: %s', listId, { storedState });

      if (storedState) {
        // Hydrate URL from stored state
        debug('Hydrating URL from stored state', { listId, storedState });
        setAllParams({
          ...storedState.filters,
          pageIndex: storedState.pageIndex,
          pageSize: storedState.pageSize,
          sort: storedState.sort,
        });
        hasHydratedRef.current = true;
        debug('Hydration complete, hasHydratedRef set to true');
      } else {
        debug('No stored state found, using defaults');
      }
    } else {
      // URL has params - sync to storage
      const { pageIndex, pageSize, sort, ...urlFilters } = allParams;
      const stateToStore: StoredListViewState = {
        filters: urlFilters,
        sort: sort ?? null,
        pageIndex,
        pageSize,
      };
      debug('URL has params, syncing to storage', { listId, stateToStore });
      setStoredState(listId, stateToStore);
    }
  }, []); // Empty deps - only run on mount, refs prevent re-running

  // Sync URL changes to storage (after initial mount)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Skip sync on initial mount if we just hydrated (to avoid overwriting)
    if (isInitialMountRef.current || hasHydratedRef.current) {
      debug('Skipping sync (initial mount or just hydrated)', {
        isInitialMount: isInitialMountRef.current,
        hasHydrated: hasHydratedRef.current,
      });
      hasHydratedRef.current = false;
      return;
    }

    // Extract filters from allParams
    const { pageIndex, pageSize, sort, ...urlFilters } = allParams;
    const stateToStore: StoredListViewState = {
      filters: urlFilters,
      sort: sort ?? null,
      pageIndex,
      pageSize,
    };

    debug('Syncing URL changes to storage', {
      listId,
      stateToStore,
      allParams,
    });

    // Sync current URL state to storage
    setStoredState(listId, stateToStore);
  }, [allParams, enabled, listId, setStoredState]);

  // Return object with clear function
  return {
    clearStoredState: () => {
      debug('Clearing stored state for listId: %s', listId);
      clearStoredState(listId);
    },
  };
}
